import useTheme from "../../../../common/hooks/useTheme";
import CompletedSvg from "../../../../common/svgs/completedSvg";
import PendingSvg from "../../../../common/svgs/PendingSvg";
import InProgressSvg from "../../../../common/svgs/InProgressSvg";
import { formatDisplayDate } from "../../../../common/utils/date";
import StatusBadge from "../common/StatusBadge";
import { styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import { userStatus } from "../../store/helper";

const UserProgress = () => {
  const theme = useTheme();
  // Optional-chained because the report (GetReportv2) is now loaded by the page
  // rather than before the workspace renders, so `dashboard` is undefined on
  // first paint. The Dashboard also hides this behind a skeleton until the
  // report lands; the fallback here keeps the component from crashing the whole
  // route if it is ever rendered before that.
  const reportData = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.dashboard?.status
  );

  if (!reportData) {
    return null;
  }

  const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip
      arrow
      {...props}
      classes={{ popper: className }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [-2, -4],
              },
            },
          ],
        },
      }}
      children={props.children}
      sx={{}}
    />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 264,
      borderRadius: "8px",
      padding: "12px",
      boxShadow: "var(--box-shadow-general)",
      backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    },
    [`& .${tooltipClasses.arrow}::before`]: {
      backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    },
  }));

  return (
    <div style={styles.container(theme)}>
      {reportData.map((item, index: number) => (
        <div key={index}>
          <CustomWidthTooltip
            title={
              <div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <StatusBadge status={item.status} />
                  {!(item.updated === "" && item.created === "") && (
                    <div style={styles.date(theme)}>
                      {formatDisplayDate(item.updated ?? item.created)}
                    </div>
                  )}
                </div>
                <div style={styles.text(theme)}>{item.description}</div>
              </div>
            }
          >
            <div style={styles.cardContainer}>
              <div style={styles.iconContainer(theme)}>
                {(item.status === userStatus.None ||
                  item.status === userStatus.Pending) && (
                  <PendingSvg
                    size={24}
                    fill={
                      item.status === userStatus.None
                        ? theme.colors_ds.NEUTRAL_ICON_WEAK
                        : theme.colorTokens.W[80]
                    }
                  />
                )}
                {item.status === userStatus.InProgress && (
                  <InProgressSvg size={24} />
                )}
                {item.status === userStatus.Completed && (
                  <CompletedSvg size={24} />
                )}
              </div>
              <div style={styles.cardTitle(theme)}>{item.label}</div>
            </div>
          </CustomWidthTooltip>
        </div>
      ))}
    </div>
  );
};

export default UserProgress;

const styles = {
  container: (theme: any) => ({
    margin: "16px 150px 0",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "space-between",
    gap: "20px",
    flex: 1,
  }),
  cardContainer: {
    marginTop: "-12px",
    width: "24px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    position: "relative" as "relative",
  },
  cardTitle: (theme: any) => ({
    width: "250px",
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    textAlign: "center" as "center",
  }),
  iconContainer: (theme: any) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "var(--box-shadow-general)",
    borderRadius: "50%",
  }),
  text: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "17px",
  }),
  date: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
